import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, effect, inject, OnInit, PLATFORM_ID, signal, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Params, Router, RouterModule, RouterOutlet } from '@angular/router';
import { finalize, take } from 'rxjs';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { SharedService } from '../shared/services/shared.service';
import { User } from '../model/user.model';
import { ToasterService } from '../shared/services/toaster.service';
import { TokenService } from '../auth/token.service';
import { HeaderComponent } from './header/header.component';
import { ChatBoxComponent } from '../modules/chat-box/chat-box.component';
import { NgbModal, NgbModalModule, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../../environments/environment';
import { CourseService } from '../services/course.service';
import { PersonalizationService } from '../services/personalization.service';
import { ScrollChainingDirective } from '../shared/directives/scroll-chaining.directive';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-layout',
    imports: [
        RouterOutlet,
        RouterModule,
        CommonModule,
        // AuthModalComponent,
        HeaderComponent,
        ChatBoxComponent,
        NgbModalModule,
        ScrollChainingDirective
    ],
    providers: [],
    templateUrl: './layout.component.html',
    styleUrl: './layout.component.scss'
})
export class LayoutComponent implements OnInit {
  claimModalRef: NgbModalRef | undefined;
  // authPage = 'login';
  // openModal = false;
  thisYear = new Date().getFullYear();
  @ViewChild('courseInvitationModal', { static: true }) courseInvitationModal: TemplateRef<unknown> | undefined;
  expandMenu = false;
  user = signal<User | null>(null);
  readonly prevUrl = signal('');
  readonly currentUrl = signal('');
  readonly isMobileMenuActive = signal(false);
  readonly isSignedIn = signal(false);
  readonly activeTab = signal('');
  readonly hideExceptTopMenu = signal(false);
  readonly inEmbeddedBrowser = signal(false);
  readonly loadCaddyData = signal(false);
  readonly mobileSlideMenu = signal(false);
  readonly invitedBusinessName = signal('');
  readonly claimModalSubmitting = signal(false);
  readonly businessClaimed = signal(false);

  private _sharedService = inject(SharedService);
  private router = inject(Router);
  private platformId = inject<object>(PLATFORM_ID);
  private _activatedRoute = inject(ActivatedRoute);
  private _toaster = inject(ToasterService);
  private _tokenService = inject(TokenService);
  private _cdr = inject(ChangeDetectorRef);
  private _destroyRef = inject(DestroyRef);
  private _modalService = inject(NgbModal);
  private _courseService = inject(CourseService);

  protected readonly personalization = inject(PersonalizationService);
  protected readonly isLandingRoute = signal(false);

  private readonly queryParams = toSignal(this._activatedRoute.queryParams, { initialValue: {} as Params });
  private claimModalPromptHandled = false;

  private syncLandingScrollbarTheme(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Keep the fixed custom scrollbar on landing routes. Colours are no longer
    // course-driven, so no theme classes or CSS variables are applied here.
    document.documentElement.classList.toggle('landing-scrollbar-theme', this.isLandingRoute());
  }

  constructor() {
    // Open the claim modal once when the user is signed in and arrived via a
    // course-invitation link. Query params are read as a signal so the effect
    // doesn't stack a new queryParams subscription on every login-state change.
    effect(() => {
      this.isSignedIn.set(this._sharedService.loggedIn());
      if (this.isSignedIn() && this.queryParams()['source'] === 'course-invitation' && !this.claimModalPromptHandled) {
        this.claimModalPromptHandled = true;
        this.openClaimModal();
      }
    });

    // this._sharedService.openLoginModal$.subscribe(modal => {
    //   if (modal.status && this.authModal) {
    //     this.openAuthModal(this.authModal, modal.window);
    //   }
    // })
    this.router.events
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((val) => {
      if (isPlatformBrowser(this.platformId)) {
        if (val instanceof NavigationEnd) {
          this.isLandingRoute.set(val.urlAfterRedirects === '/' || val.urlAfterRedirects.startsWith('/home'));
          this.syncLandingScrollbarTheme();
          const navigation = this.router.currentNavigation();
          this.hideExceptTopMenu.set(navigation?.extras.state?.['hideExceptTopMenu'] || false);
          this._cdr.markForCheck();
          this.expandMenu = false;
          this.mobileSlideMenu.set(false);
          this.isMobileMenuActive.set(false);
          if (!val.url.includes('/settings')) {
            this.activeTab.set('');
          } else if (val.url.includes('?tab=MyBag')) {
            this.activeTab.set('MyBag');
          } else if (val.url.includes('?tab=MyPartner')) {
            this.activeTab.set('MyPartner');
          }
        }
      }
    });
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this._activatedRoute.queryParams
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(params => {
        if (params['error']) {
          this._toaster.error(params['error'], 'Please try again');
          this.clearQueryParams();
        }
        if (params['page'] === 'register') {
          if (params['access_token'] && params['id_token'] && params['refresh_token']) {
            this._tokenService.saveTokens({
              AccessToken: params['access_token'],
              IdToken: params['id_token'],
              RefreshToken: params['refresh_token']
            });
            this.loadCaddyData.set(true);
            // this.getUserSetting();
          }
        }
        if (params['type'] === 'register') {
          this.router.navigate(['/register']);
        } else if (params['type'] === 'login') {
          this.router.navigate(['/login']);
        }
        // Redirect already logged-in users to course portal
        if (params['source'] === 'course-portal' && this._sharedService.loggedIn()) {
          window.location.href = environment.coursePortalUrl;
        }
      });
      if (this.isEmbeddedBrowser()) {
        this.inEmbeddedBrowser.set(true);
      }

      this._sharedService.isMyBagActive$
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(res => {
          if (res) {
            this.activeTab.set(res);
          }
        })
    }
  }

  clearQueryParams() {
    this.router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: {},
      queryParamsHandling: ''
    });
  }

  navigateToSettingPage(tabName: string) {
    // this._sharedService.setByBagSettingTab(tabName);
    // this.router.navigateByUrl("/settings");
    this.router.navigate(["settings/user-settings"], {
      queryParams: { tab: tabName }, // Set the new query parameter
      queryParamsHandling: 'merge', // Merge with existing query params
    });
    this.activeTab.set(tabName);
    this.expandMenu = false;
    this.mobileSlideMenu.set(false);
    this.isMobileMenuActive.set(false);
  }

  isEmbeddedBrowser(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor;

    // Detect LinkedIn, Facebook, Instagram embedded browsers
    return /FBAN|FBAV|Instagram|LinkedIn/i.test(userAgent);
  }

  // openAuthModal(content: any, modalPage: string) {
  //   if (isPlatformBrowser(this.platformId)) {
  //     if (this.router.url.includes('answer') || this.router.url.includes('discover')) {
  //       localStorage.setItem('redirectUrl', this.router.url);
  //     }
  //     this.authPage = modalPage;
  //     this.authModalRef = this._modalService.open(content, { windowClass: 'auth-modal' });
  //     this.clearQueryParams();
  //   }
  // }

  // closeAuthModal(event: any) {
  //   this.authModalRef?.dismiss();
  // }

    openClaimModal() {
    if (isPlatformBrowser(this.platformId)) {
      this.invitedBusinessName.set(this._sharedService.getLocalStorage('invitedBusinessName') || '');
      this.claimModalRef = this._modalService.open(
        this.courseInvitationModal,
        { windowClass: 'claim-modal', backdrop: 'static', centered: true });
      // this.clearQueryParams();
    }
  }

  onClaimNow() {
    const inviteSessionToken = this._sharedService.getLocalStorage('inviteSessionToken') || '';
    if (!inviteSessionToken) {
      this._toaster.error('Invalid or expired invitation. Please try the invitation link again.', 'Error');
      return;
    }
    this.claimModalSubmitting.set(true);
    this._courseService
      .claimCourse(inviteSessionToken)
      .pipe(
        finalize(() => { this.claimModalSubmitting.set(false); }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => {
          this._toaster.success('Course claimed successfully', 'Success');
          this.businessClaimed.set(true);
          this.clearQueryParams();
          // Mark user as having course access
          this._sharedService.user$.pipe(take(1)).subscribe((u) => {
            if (u) {
              this._sharedService.setUser({ ...u, hasCourse: true });
            }
          });
          this._sharedService.removeFromLocalStorage('inviteSessionToken');
          this._sharedService.removeFromLocalStorage('invitedBusinessName');
        },
        error: (error) => {
          // this._toaster.error('Failed to claim course. Please try again later.', 'Error');
        }
      });
  }

  closeClaimModal() {
    this.claimModalRef?.dismiss();
  }

  goToCoursePortal() {
    this.closeClaimModal();
    window.open(environment.coursePortalUrl, '_blank');
  }
}
